import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User, Mail, Phone, Shield, AlertCircle, CheckCircle2, Edit2, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { phoneCountryCodes } from '@/lib/phoneCountryCodes';
import { useAuth } from '../contexts/AuthContext';
import { userAPI } from '../services/api';
import AppHeader from '../components/layout/AppHeader';
import { OAuthAccountLinking } from '@/components/profile/OAuthAccountLinking';
import MyTalentsCard from '@/components/profile/MyTalentsCard';

const Profile: React.FC = () => {
  const { user, refreshUser, updateUser, hasRole } = useAuth();
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string; glemtPassord?: boolean } | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editingUser, setEditingUser] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phoneNumber: user?.phoneNumber || '',
    phoneCountryCode: user?.phoneCountryCode || '+47',
    currentPassword: '',
  });
  const [saving, setSaving] = useState(false);

  // Oppdater editingUser når user endres
  useEffect(() => {
    if (user) {
      setEditingUser({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        phoneCountryCode: user.phoneCountryCode || '+47',
        currentPassword: '',
      });
    }
  }, [user]);

  // Sjekk for success/error i URL
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    
    if (success === 'account_linked') {
      setMessage({ type: 'success', text: 'Kontoen ble lenket!' });
      refreshUser();
      // Fjern query params
      window.history.replaceState({}, '', '/profile');
      
      // Fjern melding automatisk etter 3 sekunder
      const timer = setTimeout(() => {
        setMessage(null);
      }, 3000);
      
      return () => clearTimeout(timer);
    } else if (error === 'link_failed') {
      setMessage({ type: 'error', text: 'Kunne ikke lenke kontoen. Prøv igjen.' });
      // Fjern query params
      window.history.replaceState({}, '', '/profile');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]); // Kun lytt til searchParams-endringer

  const handleSave = async () => {
    if (!user) return;

    try {
      setSaving(true);
      setMessage(null);

      // Valider telefonnummer
      const normalizedPhoneNumber = editingUser.phoneNumber.replace(/\D/g, '');
      if (!normalizedPhoneNumber || normalizedPhoneNumber.trim() === '') {
        setMessage({ type: 'error', text: 'Telefonnummer er påkrevd' });
        setSaving(false);
        return;
      }

      const updateData: any = {
        firstName: editingUser.firstName.trim(),
        lastName: editingUser.lastName.trim(),
        phoneNumber: normalizedPhoneNumber,
        phoneCountryCode: editingUser.phoneCountryCode.trim(),
      };

      // Hvis e-post er endret, legg til currentPassword hvis det er oppgitt
      // Backend vil validere om passord kreves basert på om brukeren har passord
      if (editingUser.email.trim() !== user.email) {
        updateData.email = editingUser.email.trim();
        if (editingUser.currentPassword) {
          updateData.currentPassword = editingUser.currentPassword;
        }
      }

      // Sjekk om det faktisk er endringer
      const hasChanges = 
        updateData.firstName !== user.firstName ||
        updateData.lastName !== user.lastName ||
        updateData.phoneNumber !== (user.phoneNumber || '').replace(/\D/g, '') ||
        updateData.phoneCountryCode !== (user.phoneCountryCode || '+47') ||
        (updateData.email && updateData.email !== user.email);

      if (!hasChanges) {
        setMessage({ type: 'error', text: 'Ingen endringer å lagre' });
        setSaving(false);
        return;
      }

      const response = await userAPI.updateMe(updateData);
      
      // Oppdater brukeren direkte fra responsen (raskere enn refreshUser)
      if (response.user) {
        updateUser(response.user);
      }
      
      setMessage({ type: 'success', text: 'Profil oppdatert!' });
      setEditMode(false);
    } catch (error: any) {
      console.error('Update error:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || 'Kunne ikke oppdatere profil';
      const manglerPassord = error.response?.data?.manglerPassord === true;

      if (manglerPassord) {
        // Kontoen har ikke passord (bare OAuth): vis serverens melding og vei videre
        setMessage({ type: 'error', text: errorMessage, glemtPassord: true });
      } else if (error.response?.data?.requiresPassword && editingUser.email !== user?.email) {
        // Backend krever passord for e-postendring
        setMessage({
          type: 'error',
          text: 'Du må bekrefte med nåværende passord for å endre e-post'
        });
      } else {
        setMessage({
          type: 'error',
          text: errorMessage
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setEditingUser({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        phoneCountryCode: user.phoneCountryCode || '+47',
        currentPassword: '',
      });
    }
    setEditMode(false);
    setMessage(null);
  };

  // Callback for å håndtere meldinger fra OAuth-komponenten
  const handleOAuthMessage = (msg: { type: 'success' | 'error'; text: string }) => {
    setMessage(msg);
    // Fjern melding automatisk etter 3 sekunder
    setTimeout(() => {
      setMessage(null);
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Toast notifications */}
        {message && (
          <div className="fixed top-4 right-4 z-[100] max-w-md animate-in slide-in-from-top-2 fade-in duration-300">
            <div className={`flex items-center gap-2 p-3 sm:p-4 backdrop-blur-sm border shadow-lg rounded-lg text-sm ${
              message.type === 'success' 
                ? 'bg-green-600/95 border-green-500 text-white' 
                : 'bg-destructive/95 border-destructive text-white'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
              ) : (
                <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
              )}
              <span className="flex-1 font-medium">{message.text}</span>
              {message.glemtPassord && (
                <a href="/forgot-password" className="shrink-0 font-semibold underline">
                  Glemt passord?
                </a>
              )}
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setMessage(null)} 
                className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-white/20 shrink-0 text-white"
              >
                <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Min profil</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Administrer din brukerinformasjon og koblede kontoer
          </p>
        </div>

        {/* Brukerinformasjon */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Brukerinformasjon
                </CardTitle>
                <CardDescription>
                  Din registrerte informasjon i systemet
                </CardDescription>
              </div>
              {!editMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditMode(true)}
                  className="gap-2"
                >
                  <Edit2 className="h-4 w-4" />
                  Rediger
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {editMode ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">Fornavn</Label>
                    <Input
                      id="firstName"
                      value={editingUser.firstName}
                      onChange={(e) => setEditingUser({ ...editingUser, firstName: e.target.value })}
                      disabled={saving}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Etternavn</Label>
                    <Input
                      id="lastName"
                      value={editingUser.lastName}
                      onChange={(e) => setEditingUser({ ...editingUser, lastName: e.target.value })}
                      disabled={saving}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-1">
                      <Mail className="h-3.5 w-3.5" />
                      E-post
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={editingUser.email}
                      onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                      disabled={saving}
                    />
                    {editingUser.email !== user?.email && (
                      <div className="space-y-2 mt-2">
                        <Label htmlFor="currentPassword" className="text-xs text-muted-foreground">
                          {user?.googleId || user?.facebookId 
                            ? 'Hvis du har passord, oppgi det for å endre e-post'
                            : 'Bekreft med nåværende passord for å endre e-post'}
                        </Label>
                        <Input
                          id="currentPassword"
                          type="password"
                          placeholder="Nåværende passord (kan være tomt hvis du kun bruker OAuth)"
                          value={editingUser.currentPassword}
                          onChange={(e) => setEditingUser({ ...editingUser, currentPassword: e.target.value })}
                          disabled={saving}
                        />
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phoneNumber" className="flex items-center gap-1">
                      <Phone className="h-3.5 w-3.5" />
                      Telefon
                    </Label>
                    <div className="flex gap-2">
                      <Select
                        value={editingUser.phoneCountryCode}
                        onValueChange={(value) => setEditingUser({ ...editingUser, phoneCountryCode: value })}
                        disabled={saving}
                      >
                        <SelectTrigger id="phoneCountryCode" className="w-28">
                          <SelectValue placeholder="+47" />
                        </SelectTrigger>
                        <SelectContent>
                          {phoneCountryCodes.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                              {country.flag} {country.code} {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        id="phoneNumber"
                        value={editingUser.phoneNumber}
                        onChange={(e) => setEditingUser({ ...editingUser, phoneNumber: e.target.value })}
                        disabled={saving}
                        className="flex-1"
                        placeholder="12345678"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 justify-end pt-2">
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    disabled={saving}
                    className="gap-2"
                  >
                    <X className="h-4 w-4" />
                    Avbryt
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="gap-2"
                  >
                    <Check className="h-4 w-4" />
                    {saving ? 'Lagrer...' : 'Lagre'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Navn</p>
                  <p className="text-base font-medium">{user?.firstName} {user?.lastName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5" />
                    E-post
                  </p>
                  <p className="text-base font-medium">{user?.email}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" />
                    Telefon
                  </p>
                  <p className="text-base font-medium">
                    {user?.phoneNumber 
                      ? `${user?.phoneCountryCode || '+47'} ${user.phoneNumber}`
                      : <span className="text-muted-foreground italic">Ikke satt</span>}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <Shield className="h-3.5 w-3.5" />
                    Roller
                  </p>
                  <div className="flex gap-1 flex-wrap mt-1">
                    {user?.roles && user.roles.length > 0 ? (
                      user.roles.map((role) => (
                        <Badge key={role} variant="outline" className="text-xs">
                          {role}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground">Ingen roller</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {user && !hasRole('superadmin') && <MyTalentsCard userId={user.id} />}

        {/* OAuth Koblinger - Bruker ny komponent */}
        <OAuthAccountLinking onMessage={handleOAuthMessage} />
      </div>
    </div>
  );
};

export default Profile;

